// trade-planet-admin/src/router/index.js
import { createRouter, createWebHistory, createWebHashHistory } from "vue-router";
import { useMainStore } from "@/stores/main";
import { rolePermissions } from "@/config/rolePermissionsV2";

// Lazy Load Components
const HomeView = () => import("@/views/HomeView.vue");
const SettingsView = () => import("@/views/SettingsView.vue");


const AllRaresView = () => import("@/views/AllRaresView.vue");
const RareItemValuesView = () => import("@/views/RareItemValuesView.vue");
const AddRareItemView = () => import("@/views/AddRareItemView.vue");
const EditRareItemView = () => import("@/views/EditRareItemView.vue");
const RareItemValueEditView = () => import("@/views/EditRareItemValueView.vue");
const FastAddRareItemView = () => import("@/views/FastAddRareItem.vue");

const AllItemsView = () => import("@/views/AllItemsView.vue");

const ItemsUpdateLogsView = () => import("@/views/ItemsUpdateLogsView.vue");
const ItemsValueUpdateLogsView = () => import("@/views/ItemsValueUpdateLogsView.vue");

const AdminEditLog = () => import("@/views/AdminEditLog.vue");

const AllAnnouncementsView = () => import("@/views/AllAnnouncementsView.vue");
const AddAnnouncementView = () => import("@/views/AddAnnouncementView.vue");
const EditAnnouncementView = () => import("@/views/EditAnnouncementView.vue");
const AnnouncementLogsView = () => import("@/views/AnnouncementLogsView.vue");

const AllAdsView = () => import("@/views/AllAdsView.vue");
const AddAdView = () => import("@/views/AddAdView.vue");
const EditAdView = () => import("@/views/EditAdView.vue");
const AdLogsView = () => import("@/views/AdLogsView.vue");

/*
const AllUsersView = () => import("@/views/AllUsersView.vue");
const AddUserView = () => import("@/views/AddUserView.vue");
const AddUserItemView = () => import("@/views/AddUserItemView.vue");
const EditUserView = () => import("@/views/EditUserView.vue");
const AddUserInventoryView = () => import("@/views/UserInventoryAddUpdateView.vue");
const UsersUpdateLogsView = () => import("@/views/UsersUpdateLogsView.vue");
*/

const EditTypeDecodeView = () => import("@/views/EditTypeDecodeView.vue");
const UiView = () => import("@/views/UiView.vue");
const TablesListsView = () => import("@/views/TablesListsView.vue");
const UiBasicView = () => import("@/views/UiBasicView.vue");
const FormsView = () => import("@/views/FormsView.vue");
const FormsAdvView = () => import("@/views/FormsAdvView.vue");
const StyleView = () => import("@/views/StyleView.vue");
const LoginView = () => import("@/views/LoginView.vue");
const ErrorView = () => import("@/views/ErrorView.vue");

const routes = [

  { path: "/", redirect: "/dashboard" },

  // Public Pages
  { path: "/dashboard", name: "dashboard", component: HomeView, meta: { title: "Dashboard", requiresAuth: true } },
  { path: "/settings", name: "Settings", component: SettingsView, meta: { title: "Settings", requiresAuth: true } },

  // Item Management
  {
    path: "/rare/items",
    name: "All Rares List",
    component: AllRaresView,
    meta: {
      title: "All Rares List",
      requiresAuth: true,
      requiresPermission: ["view_items", "edit_rap", "edit_value"],
      permissionMode: "any",
    }
  },
  {
    path: "/rare/item/values",
    name: "Rare Item Values",
    component: RareItemValuesView,
    meta: {
      title: "Rare Item Values",
      requiresAuth: true,
      requiresPermission: ["edit_rap", "edit_value", "verify_rap", "verify_value"],
      permissionMode: "any",
    }
  },
  {
    path: "/add/rare/item",
    name: "Add Item",
    component: AddRareItemView,
    meta: {
      title: "Add Rare Item",
      requiresAuth: true,
      requiresPermission: "edit_items"
    }
  },
  {
    path: "/edit/rare/item/value/:itemId",
    name: "Edit Item Value",
    component: RareItemValueEditView,
    meta: {
      title: "Edit Item Value",
      requiresAuth: true,
      requiresPermission: ["edit_rap", "edit_value", "verify_rap", "verify_value"],
      permissionMode: "any",
    }
  },
  {
    path: "/edit/rare/item/:itemId",
    name: "Edit Item",
    component: EditRareItemView,
    meta: {
      title: "Edit Item",
      requiresAuth: true,
      requiresPermission: "edit_items",
    }
  },
  {
    path: "/fast/add/rare/item",
    name: "Fast Add Rare Item",
    component: FastAddRareItemView,
    meta: {
      title: "Fast Add Rare Item",
      requiresAuth: true,
      requiresPermission: "edit_items"
    }
  },
  {
    path: "/items",
    name: "All Items List",
    component: AllItemsView,
    meta: {
      title: "All Items List",
      requiresAuth: true,
      requiresPermission: "edit_items"
    }
  },
  {
    path: "/all/items/logs",
    name: "Items Update Logs",
    component: ItemsUpdateLogsView,
    meta: {
      title: "Items Update Logs",
      requiresAuth: true,
      requiresPermission: "view_logs"
    }
  },
  {
    path: "/all/items/value/logs",
    name: "Rare Items Value Update Logs",
    component: ItemsValueUpdateLogsView,
    meta: {
      title: "Rare Items Value Update Logs",
      requiresAuth: true,
      requiresPermission: "view_logs"
    }
  },

  // Announcements
  {
    path: "/announcements",
    name: "All Announcements",
    component: AllAnnouncementsView,
    meta: {
      title: "Announcements",
      requiresAuth: true,
      requiresPermission: ["view_announcements", "edit_announcements"],
      permissionMode: "any",
    }
  },
  {
    path: "/add/announcement",
    name: "Add Announcement",
    component: AddAnnouncementView,
    meta: {
      title: "Add Announcement",
      requiresAuth: true,
      requiresPermission: "edit_announcements"
    }
  },
  {
    path: "/edit/announcement/:id",
    name: "Edit Announcement",
    component: EditAnnouncementView,
    meta: {
      title: "Edit Announcement",
      requiresAuth: true,
      requiresPermission: "edit_announcements"
    }
  },
  {
    path: "/announcements/logs",
    name: "Announcement Logs",
    component: AnnouncementLogsView,
    meta: {
      title: "Announcement Logs",
      requiresAuth: true,
      requiresPermission: ["view_logs", "view_announcements", "edit_announcements"],
      permissionMode: "any",
    }
  },

  // Ads
  {
    path: "/ads",
    name: "All Ads",
    component: AllAdsView,
    meta: {
      title: "Ads",
      requiresAuth: true,
      requiresPermission: ["view_ads", "edit_ads"],
      permissionMode: "any",
    }
  },
  {
    path: "/add/ad",
    name: "Add Ad",
    component: AddAdView,
    meta: {
      title: "Add Ad",
      requiresAuth: true,
      requiresPermission: "edit_ads"
    }
  },
  {
    path: "/edit/ad/:id",
    name: "Edit Ad",
    component: EditAdView,
    meta: {
      title: "Edit Ad",
      requiresAuth: true,
      requiresPermission: "edit_ads"
    }
  },
  {
    path: "/ads/logs",
    name: "Ad Logs",
    component: AdLogsView,
    meta: {
      title: "Ad Logs",
      requiresAuth: true,
      requiresPermission: ["view_logs", "view_ads", "edit_ads"],
      permissionMode: "any",
    }
  },

  // User Management

  // Admin Moderation
  {
    path: "/admin/:id/edit/logs",
    name: "Admin User Edit Log",
    component: AdminEditLog,
    meta: {
      title: "Admin Edit Logs",
      requiresAuth: true,
      requiresPermission: ["moderate_users"]
    }
  },

  // Moderation
  { path: "/helper/edit-type/decode", name: "Moderation", component: EditTypeDecodeView, meta: { title: "User Moderation", requiresAuth: true, requiresPermission: "moderate_users" } },

  // UI & Auth
  { path: "/tables-and-lists", name: "tables", component: TablesListsView, meta: { title: "Tables Lists View", requiresAuth: true } },
  { path: "/forms", name: "forms", component: FormsView, meta: { title: "Forms", requiresAuth: true } },
  { path: "/forms-advanced", name: "forms advanced", component: FormsAdvView, meta: { title: "Forms Advanced", requiresAuth: true } },
  { path: "/ui", name: "ui", component: UiView, meta: { title: "UI", requiresAuth: true } },
  { path: "/ui-advanced", name: "ui advanced", component: UiBasicView, meta: { title: "Ui Advanced", requiresAuth: true } },
  { path: "/styles", name: "styles", component: StyleView, meta: { title: "Styles", requiresAuth: true } },
  { path: "/login", name: "login", component: LoginView, meta: { title: "Login" } },
  { path: "/error", name: "error", component: ErrorView, meta: { title: "Error" } },

  // Catch-all
  { path: "/:pathMatch(.*)*", redirect: "/error?type=notfound" }
]

const useHash = process.env.VUE_APP_ROUTER_MODE === "hash";

const router = createRouter({
  history: useHash ? createWebHashHistory() : createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 };
  },
});

const MAIN_SITE_URL = process.env.VUE_APP_MAIN_SITE_URL || "https://trade-planet.net";

function redirectToMainSite() {
  window.location.href = MAIN_SITE_URL;
}

router.beforeEach(async (to, from, next) => {
  const mainStore = useMainStore();

  // The auth token lives in an httpOnly cookie, so it can't be read here to
  // check "are we logged in" up front — ensureUser() asks the API once per
  // session (de-duping concurrent navigations) and a 401 means "not logged in."
  try {
    await mainStore.ensureUser();
  } catch (error) {
    console.error("⚠ Failed to fetch user:", error);
  }

  const isAuthenticated = !!mainStore.userName;

  if (to.meta.requiresAuth && !isAuthenticated) {
    redirectToMainSite();
    return;
  }

  if (to.path === "/error" && !isAuthenticated) {
    redirectToMainSite();
    return;
  }

  if (to.path === "/login" && isAuthenticated) {
    return next({ path: "/dashboard" });
  }

  if (to.meta.requiresPermission) {
    const userRole = mainStore.userRole;
    const required = Array.isArray(to.meta.requiresPermission) ? to.meta.requiresPermission : [to.meta.requiresPermission];
    const userPerms = rolePermissions[userRole] || [];
  
    const mode = to.meta.permissionMode || "all";

    const ok =
      mode === "any"
        ? required.some((perm) => userPerms.includes(perm))
        : required.every((perm) => userPerms.includes(perm));
    
    if (!ok) {
      console.warn(
        `⚠ Access Denied for ${to.path} | role=${userRole} | mode=${mode} | required=${required.join(
          ","
        )} | userPerms=${userPerms.join(",")}`
      );
      return next({ path: "/error", query: { type: "restricted" } });
    }
  
    //console.log(`Access Granted to '${userRole}' for ${to.path}`);
  }

  next();
});

router.onError((err) => {
  console.error("Router error:", err);
  if (router.currentRoute.value.path !== "/error") {
    router.push({ path: "/error", query: { type: "notfound" } });
  }
});

export function setupRouter() {
  const mainStore = useMainStore();
  mainStore.setupAxiosInterceptors();
}

export default router;